import { createRoot } from 'react-dom/client';
import './index.css';
// Imported for its side effect: initialises i18next and starts the
// languageChanged listener that keeps <html lang/dir> in step. Must come
// before App, which reads the resolved language to pick MUI's direction.
import './i18n';
import { stripLanguagePrefix } from './i18n/config';
import App from './App';
import { BrowserRouter } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { TechnicianAuthProvider } from './context/TechnicianAuthContext';
import { SocketProvider } from './context/SocketContext';
import { NotificationProvider } from './context/NotificationContext';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';

const rootElement = document.getElementById('root');

const app = (
  <BrowserRouter>
    <AuthProvider>
      <TechnicianAuthProvider>
        <SocketProvider>
          <NotificationProvider>
            <App />
          </NotificationProvider>
        </SocketProvider>
      </TechnicianAuthProvider>
    </AuthProvider>
  </BrowserRouter>
);

// react-snap prerenders a few routes to static HTML at build time (see the
// "postbuild" script + "reactSnap" config in package.json). Crawlers and the
// first paint get real content; React then mounts over it.
//
// Deliberately createRoot, never hydrateRoot.
//
// react-snap serialises the live DOM, and outerHTML merges adjacent text nodes.
// React keeps them separate — `…our{" "}<Link>` is three children, not two — and
// real SSR preserves that boundary with a <!-- --> separator that react-snap
// cannot emit. So every `{" "}` beside a link is a hydration mismatch. Measured
// on /terms: the snapshot has 86 nodes where React renders 87.
//
// React's answer to a mismatch is to discard the whole tree and re-render
// anyway, after logging errors #418/#423. Mounting fresh reaches the same DOM
// without the console noise or the wasted reconciliation pass.
const SNAP_ATTR = 'data-snap-path';
const snapPath = document.documentElement.getAttribute(SNAP_ATTR);
// Written before rendering so react-snap stamps each snapshot with its route.
document.documentElement.setAttribute(SNAP_ATTR, window.location.pathname);

// Routes react-snap prerenders. Same import specifiers as the lazy() factories
// in App, so these resolve to the same chunks and warm webpack's module cache.
const PRERENDERED_CHUNKS = {
  '/': () => import('./pages/LandingPage'),
  '/terms': () => import('./pages/Terms/Terms'),
  '/PrivacyPolicy': () => import('./pages/PrivacyPolicy/PrivacyPolicy'),
};

const mount = () => createRoot(rootElement).render(app);

// On a prerendered route, fetch its chunk before mounting, so the visitor keeps
// looking at the real page during the network round trip instead of a spinner.
//
// It does not remove the fallback frame entirely: lazy() still suspends once on
// first render, because warming webpack's module cache does not settle the lazy
// wrapper itself. Only an eager import does that, and eager-importing the
// landing page costs 62KB gzip on EVERY route including the authenticated app —
// too much for one cosmetic frame on the marketing page.
// The map is keyed by the bare path, so '/ar/terms' finds the Terms chunk
// rather than missing and falling back to a spinner on every translated page.
const preload = PRERENDERED_CHUNKS[stripLanguagePrefix(window.location.pathname)];
if (preload && snapPath === window.location.pathname && rootElement.hasChildNodes()) {
  preload().then(mount, mount);
} else {
  mount();
}

serviceWorkerRegistration.unregister();
